import React, { useCallback, useMemo } from 'react';

import config from 'config';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { FaRegCheckCircle } from 'react-icons/fa';
import styled from 'styled-components';

import useModelSizeLabel from '~/pdp/utils/useModelSizeLabel';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import {
  GENDER_SPECIFIC_SIZES,
  MODEL_SIZES_MAP,
} from '~/shared/utils/sizeConstants';
import {
  Dropdown,
  Picture,
  useModelImageContext,
  useProductAttributeFieldType,
  AttributeFieldTypes,
  useProductContext,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import parseHeight from '../../utils/parseHeight';
import { useMemberModelContext } from '../EnhancedMemberModelProvider';

const DynamicFormattedMessage = FormattedMessage;

const DropdownItem = styled(Dropdown.ListItem)`
  ${({ theme: { pdpStyles } }) => pdpStyles.enhancedMemberModel.item.wrapper}
`;

const ModelImageWrapper = styled.div`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.item.imageWrapper}
`;

const ModelInfo = styled.div`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.item.info.wrapper}
`;
const ModelNameLabel = styled.span`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.item.info.nameLabel}
`;

const ActiveIcon = styled(FaRegCheckCircle)`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.item.info.activeIcon}
  margin-left: 5px;
`;

const ModelDimensions = styled.div`
  ${({ theme: { pdpStyles } }) => pdpStyles.enhancedMemberModel.item.dimensions}
`;

const ModelHeightLabel = styled.span`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.item.heightLabel}
`;

const maleTagId = config.get('public.productTags.maleTagId');
const allowedModelSizes = config.get('public.brand.memberModel.allowedSizes');

export default function EnhancedMemberModelItem({
  memberModel = {},
  currentModelIndex = 0,
  imageAspectRatio = 112 / 75,
}) {
  const intl = useIntl();
  const product = useProductContext();
  const { tagIdList = [], gender: productGender } = product;
  const { model, imageSources } = memberModel;
  const { setActiveModelIndex, activeModelIndex } = useModelImageContext();
  const { closeModal } = useMemberModelContext();
  const sizeCategory = useProductAttributeFieldType(AttributeFieldTypes.SIZE);
  const imageBreakpoints = [{ minWidth: 0, pictureWidth: 75 }];
  const isTaggedMale = tagIdList.find((tagId) => maleTagId === tagId);
  const { tld: currentDomain } = useDomain();
  const {
    product: { productCategoryIdList },
  } = useProductDetailContext();
  const displaySize = useModelSizeLabel({
    model,
    sizeCategory,
    productCategoryIdList,
    tagIdList,
  });

  const isActiveModel = activeModelIndex === currentModelIndex;
  const [modelImage] = imageSources;

  const handleModelChange = () => {
    setActiveModelIndex(currentModelIndex);
    closeModal();
  };

  const filterBySizeMapKey = useCallback(
    (sizeMapKey) => {
      // filter out brand specific size displays
      if (!allowedModelSizes.includes(sizeMapKey)) {
        return false;
      }
      // filter out sizes that are not applicable to men
      if (isTaggedMale && GENDER_SPECIFIC_SIZES.female.includes(sizeMapKey)) {
        return false;
      }
      return !!model[MODEL_SIZES_MAP[sizeMapKey]?.key];
    },
    [isTaggedMale, model]
  );

  const modelDimensions = useMemo(() => {
    return Object.keys(MODEL_SIZES_MAP)
      .filter(filterBySizeMapKey)
      .map((sizeMapKey) => {
        const rbId = getBrandValue({
          savage: `pdp_change_model.${productGender}_${MODEL_SIZES_MAP[sizeMapKey].rbid}`,
          default: `pdp_change_model.${MODEL_SIZES_MAP[sizeMapKey].rbid}`,
        });
        return (
          <span key={sizeMapKey}>
            <DynamicFormattedMessage
              id={rbId}
              defaultMessage="{sizeCategory}"
              values={{
                sizeCategory: MODEL_SIZES_MAP[sizeMapKey].label,
              }}
            />
            {': '}
            <b>{model[MODEL_SIZES_MAP[sizeMapKey].key]}</b>
          </span>
        );
      });
  }, [filterBySizeMapKey, productGender, model]);

  if (isEmpty(memberModel) || !model) {
    return null;
  }

  const { feet, inches, meters } = parseHeight(model.heightInInches);
  const modelHeight =
    currentDomain === '.com' || currentDomain === '.co.uk'
      ? `: ${feet}'${inches}''`
      : `: ${meters}m`;

  return (
    <DropdownItem
      value={model.firstName}
      onClick={handleModelChange}
      aria-label={intl.formatMessage({
        id: 'site_pdp.member_model_item_aria',
        defaultMessage: 'Model selection',
      })}
      $isActive={isActiveModel}
      data-autotag="pdp-modal-styledSelect-option"
    >
      <ModelImageWrapper $isActive={isActiveModel}>
        <Picture
          sources={modelImage}
          breakpoints={imageBreakpoints}
          aspectRatio={imageAspectRatio}
        />
      </ModelImageWrapper>
      <ModelInfo>
        <ModelNameLabel>
          {`${model.firstName} - `}
          <b>
            <FormattedMessage
              id="pdp_change_model.wearing_size"
              defaultMessage="Wearing Size"
            />
            {`: ${displaySize}`}
          </b>

          {isActiveModel && <ActiveIcon />}
        </ModelNameLabel>
        <ModelHeightLabel>
          <FormattedMessage
            id="pdp_change_model.height_label"
            defaultMessage="Height"
            values={{
              ...parseHeight(model.heightInInches),
            }}
          />
          <b>{modelHeight}</b>
        </ModelHeightLabel>
        <ModelDimensions>{modelDimensions}</ModelDimensions>
      </ModelInfo>
    </DropdownItem>
  );
}

EnhancedMemberModelItem.propTypes = {
  currentModelIndex: PropTypes.number,
  imageAspectRatio: PropTypes.number,
  memberModel: PropTypes.object,
};
